import { LBtn, LModal, LTextareaField } from "@components";
import { faCheckCircle, faClipboard } from "@fortawesome/free-regular-svg-icons";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { yupResolver } from "@hookform/resolvers/yup";
import { AssessmentStatus } from "@store/generalTypes";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { translate, Translate } from "react-i18nify";
import { object, string } from "yup";

interface MarkNote {
    note?: string;
}

export const DownloadFollowupButtons = ({ ...props }: DownloadFollowupButtonsProps): JSX.Element => {
    const validateMethods = useForm<MarkNote>({
        resolver: yupResolver(
            object({
                note: string().max(500).required(translate("admin.pleaseAddAnote"))
            })
        )
    });

    const [markForOpen, setMarkForOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const toggleModal = (): void => {
        validateMethods.resetField("note");
        setMarkForOpen(!markForOpen);
    };

    const handleOnConfirm = async (): Promise<void> => {
        setLoading(true);
        const valid = await validateMethods.trigger("note");

        if (!valid) {
            setLoading(false);
            return;
        }

        try {
            await props.handleChangeStatus(validateMethods.getValues("note")!);
            toggleModal();
        } catch (er) {
            console.error(er);
        } finally {
            setLoading(false);
        }
    };

    const isReviewNeeded = props.status?.toLowerCase() === AssessmentStatus.ReviewNeeded.replace(" ", "").toLowerCase();
    const isComplete =
        props.status?.toLowerCase() === AssessmentStatus.Complete.toLowerCase() ||
        props.status?.toLowerCase() === AssessmentStatus.ReviewComplete.replace(" ", "").toLowerCase();

    return (
        <>
            <LBtn
                variation="outline"
                noSpacing
                fullWidth
                iconRight={faDownload}
                classes="mb-2"
                onClick={props.handlePrint}
            >
                <Translate value="admin.downloadPDF" />
            </LBtn>
            {isComplete && (
                <LBtn variation="outline" noSpacing fullWidth iconRight={faClipboard} onClick={toggleModal}>
                    <Translate value="admin.markForReview" />
                </LBtn>
            )}

            {isReviewNeeded && (
                <LBtn variation="outline" noSpacing fullWidth iconRight={faCheckCircle} onClick={toggleModal}>
                    <Translate value="admin.reviewCompleted" />
                </LBtn>
            )}

            {markForOpen && (
                <LModal
                    title={
                        <p className="has-text-centered has-text-weight-bold is-size-3">
                            <Translate value={isReviewNeeded ? "admin.reviewCompleted" : "admin.markForReview"} />
                        </p>
                    }
                    fullHeight={false}
                    width={"m"}
                    hasCancelConfirmButtons
                    confirmText={translate("done")}
                    onConfirm={handleOnConfirm}
                    onClose={toggleModal}
                    confirmClasses="is-danger"
                    confirmLoading={loading}
                    divider={false}
                >
                    <div className="is-flex is-flex-direction-column">
                        <>
                            {isComplete && (
                                <p className="has-text-centered mb-4">
                                    <Translate value="admin.reviewDetailTitle" />
                                </p>
                            )}
                            <FormProvider {...validateMethods}>
                                <LTextareaField
                                    label=""
                                    inputName="note"
                                    placeholder={
                                        isReviewNeeded
                                            ? translate("admin.reviewCompletePlaceholder")
                                            : translate("admin.reviewNotePlaceholder")
                                    }
                                    rows={10}
                                    maxLength={500}
                                    counter
                                    theme="dark"
                                    footer={translate("admin.invalidateReview3")}
                                />
                            </FormProvider>
                            {isReviewNeeded && (
                                <p className="has-text-centered mt-6 mb-2 has-text-weight-bold">
                                    <Translate value="admin.reviewCompleteDoneText" />
                                </p>
                            )}
                        </>
                    </div>
                </LModal>
            )}
        </>
    );
};

interface DownloadFollowupButtonsProps {
    handlePrint: () => void;
    status?: AssessmentStatus;
    handleChangeStatus: (note: string) => Promise<void>;
}
